import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  Search,
  Clock,
  MapPin,
  Users,
  Bell,
  Tag,
  Settings,
  RefreshCw,
  Eye,
  List,
  Grid,
  Loader2,
  AlertCircle,
  CheckCircle,
  Package,
  DollarSign,
  Truck,
  FileText,
  X } from
'lucide-react';
import {
  getCalendars,
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  getMyDayView } from
'../services/calendarAPI';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CATEGORY_COLORS = {
  receiving: { bg: 'bg-blue-500', text: 'text-blue-500', icon: Package },
  shipping: { bg: 'bg-green-500', text: 'text-green-500', icon: Truck },
  finance: { bg: 'bg-purple-500', text: 'text-purple-500', icon: DollarSign },
  compliance: { bg: 'bg-red-500', text: 'text-red-500', icon: FileText },
  delivery: { bg: 'bg-orange-500', text: 'text-orange-500', icon: Truck },
  maintenance: { bg: 'bg-gray-500', text: 'text-gray-500', icon: Settings },
  meeting: { bg: 'bg-indigo-500', text: 'text-indigo-500', icon: Users },
  reminder: { bg: 'bg-yellow-500', text: 'text-yellow-500', icon: Bell },
  other: { bg: 'bg-slate-500', text: 'text-slate-500', icon: CalendarIcon }
};

const PRIORITY_BADGES = {
  low: { variant: 'secondary', label: 'Low' },
  normal: { variant: 'outline', label: 'Normal' },
  high: { variant: 'warning', label: 'High' },
  critical: { variant: 'destructive', label: 'Critical' }
};

export default function CalendarPage() {
  const { t } = useTranslation(['common', 'navigation']);
  const [loading, setLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const [view, setView] = useState('month'); // month, week, day, agenda
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [myDayData, setMyDayData] = useState(null);

  // New event form state
  const [newEvent, setNewEvent] = useState({
    calendar_id: '',
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    all_day: false,
    category: 'other',
    priority: 'normal',
    location_text: ''
  });

  // Calculate date range for fetching events
  const dateRange = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    start.setDate(start.getDate() - 7); // Include previous month's trailing days
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    end.setDate(end.getDate() + 7); // Include next month's leading days
    return { start, end };
  }, [currentDate]);

  // Load calendars
  useEffect(() => {
    const loadCalendars = async () => {
      try {
        const data = await getCalendars();
        setCalendars(data);
        // Select all calendars by default
        setSelectedCalendars(data.map((c) => c.id));
      } catch (error) {
        console.error('Failed to load calendars:', error);
        toast.error('Failed to load calendars');
      }
    };
    loadCalendars();
  }, []);

  // Load events when calendars or date range changes
  useEffect(() => {
    const loadEvents = async () => {
      if (selectedCalendars.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getEvents(
          selectedCalendars,
          dateRange.start,
          dateRange.end,
          categoryFilter !== 'all' ? { category: categoryFilter } : {}
        );
        setEvents(data);
      } catch (error) {
        console.error('Failed to load events:', error);
        toast.error('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [selectedCalendars, dateRange, categoryFilter]);

  // Load My Day data
  useEffect(() => {
    const loadMyDay = async () => {
      try {
        const data = await getMyDayView();
        setMyDayData(data);
      } catch (error) {
        console.error('Failed to load My Day:', error);
      }
    };
    loadMyDay();
  }, []);

  // Navigation handlers
  const navigatePrev = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);else
      if (view === 'week') newDate.setDate(newDate.getDate() - 7);else
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const navigateNext = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);else
      if (view === 'week') newDate.setDate(newDate.getDate() + 7);else
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const goToToday = () => setCurrentDate(new Date());

  // Generate calendar grid for month view
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    // Add padding days from previous month
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(firstDay);
      date.setDate(date.getDate() - i - 1);
      days.push({ date, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i), isCurrentMonth: true });
    }

    // Add padding days for next month
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i), isCurrentMonth: false });
    }

    return days;
  }, [currentDate]);

  // Get events for a specific day
  const getEventsForDay = useCallback((date) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return events.filter((event) => {
      const eventStart = new Date(event.start_time);
      return eventStart >= dayStart && eventStart <= dayEnd;
    });
  }, [events]);

  // Handle event creation
  const handleCreateEvent = async () => {
    try {
      const eventData = {
        ...newEvent,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString(),
        calendar_id: parseInt(newEvent.calendar_id)
      };

      await createEvent(eventData);
      toast.success('Event created successfully');
      setShowCreateDialog(false);
      setNewEvent({
        calendar_id: '',
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        all_day: false,
        category: 'other',
        priority: 'normal',
        location_text: ''
      });

      // Refresh events
      const data = await getEvents(selectedCalendars, dateRange.start, dateRange.end);
      setEvents(data);
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event');
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(eventId);
      toast.success('Event deleted');
      setShowEventDialog(false);
      setSelectedEvent(null);

      // Refresh events
      const data = await getEvents(selectedCalendars, dateRange.start, dateRange.end);
      setEvents(data);
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  // Toggle calendar selection
  const toggleCalendar = (calendarId) => {
    setSelectedCalendars((prev) =>
    prev.includes(calendarId) ?
    prev.filter((id) => id !== calendarId) :
    [...prev, calendarId]
    );
  };

  // Render event badge
  const renderEventBadge = (event) => {
    const category = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.other;
    const Icon = category.icon;

    return (
      <div
        key={event.id}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedEvent(event);
          setShowEventDialog(true);
        }}
        className={`text-xs p-1 mb-1 rounded truncate cursor-pointer hover:opacity-80 transition-opacity ${category.bg} text-white`}
        data-testid={`event-badge-${event.id}`}>
        
        <div className="flex items-center gap-1">
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{event.title}</span>
        </div>
      </div>);

  };

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-6 space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-blue-600" />{t("enterpriseCalendar", "Enterprise Calendar")}

          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t("manageEventsSchedules", "Manage events, schedules, and integrations across all modules")}

          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            data-testid="today-btn">{t("today", "Today")}


          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-event-btn">
            
            <Plus className="w-4 h-4 mr-1" />{t("newEvent", "New Event")}

          </Button>
        </div>
      </div>

      {/* My Day Summary */}
      {myDayData &&
      <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{t("myDay", "My Day")}</h3>
                <p className="text-blue-100 text-sm">
                  {myDayData.summary.total_events}{t("eventsToday", "events today \u2022")}{myDayData.summary.upcoming_count}{t("upcoming", "upcoming")}
              </p>
              </div>
              {myDayData.summary.alerts_count > 0 &&
            <Badge variant="destructive" className="bg-red-500">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {myDayData.summary.alerts_count}{t("alerts", "alerts")}
            </Badge>
            }
            </div>
            
            {myDayData.upcoming.length > 0 &&
          <div className="mt-3 flex flex-wrap gap-2">
                {myDayData.upcoming.slice(0, 3).map((event) =>
            <Badge key={event.id} variant="secondary" className="bg-white/20 text-white border-0">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatTime(event.start_time)} - {event.title}
                  </Badge>
            )}
              </div>
          }
          </CardContent>
        </Card>
      }

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Calendar List & Filters */}
        <div className="space-y-4">
          {/* Calendars */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />{t("myCalendars", "My Calendars")}

              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {calendars.map((cal) =>
              <label
                key={cal.id}
                className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded-lg transition-colors">
                
                  <Checkbox
                  checked={selectedCalendars.includes(cal.id)}
                  onCheckedChange={() => toggleCalendar(cal.id)}
                  data-testid={`calendar-toggle-${cal.id}`} />
                
                  <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cal.color }} />
                
                  <span className="text-sm truncate">{cal.name}</span>
                </label>
              )}
              
              {calendars.length === 0 &&
              <p className="text-sm text-slate-500 text-center py-4">{t("noCalendarsFound", "No calendars found")}

              </p>
              }
            </CardContent>
          </Card>

          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Filter className="w-4 h-4" />{t("filters", "Filters")}

              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">{t("category", "Category")}</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="mt-1" data-testid="category-filter">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allCategories", "All Categories")}</SelectItem>
                    <SelectItem value="receiving">{t("receiving", "Receiving")}</SelectItem>
                    <SelectItem value="shipping">{t("shipping", "Shipping")}</SelectItem>
                    <SelectItem value="finance">{t("finance", "Finance")}</SelectItem>
                    <SelectItem value="compliance">{t("compliance", "Compliance")}</SelectItem>
                    <SelectItem value="delivery">{t("delivery", "Delivery")}</SelectItem>
                    <SelectItem value="meeting">{t("meeting", "Meeting")}</SelectItem>
                    <SelectItem value="other">{t("other", "Other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">{t("search", "Search")}</Label>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-events" />
                  
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Calendar Area */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                {/* Navigation */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={navigatePrev}
                      data-testid="prev-btn">
                      
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={navigateNext}
                      data-testid="next-btn">
                      
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <h2 className="text-lg font-semibold" data-testid="current-month">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                </div>

                {/* View Tabs */}
                <Tabs value={view} onValueChange={setView}>
                  <TabsList className="grid grid-cols-4 w-auto">
                    <TabsTrigger value="month" className="text-xs px-3">
                      <Grid className="w-4 h-4 mr-1" />{t("month", "Month")}

                    </TabsTrigger>
                    <TabsTrigger value="week" className="text-xs px-3">
                      <CalendarIcon className="w-4 h-4 mr-1" />{t("week", "Week")}

                    </TabsTrigger>
                    <TabsTrigger value="day" className="text-xs px-3">
                      <Clock className="w-4 h-4 mr-1" />{t("day", "Day")}

                    </TabsTrigger>
                    <TabsTrigger value="agenda" className="text-xs px-3">
                      <List className="w-4 h-4 mr-1" />{t("agenda", "Agenda")}

                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>

            <CardContent>
              {loading ?
              <div className="flex items-center justify-center h-96">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div> :

              <>
                  {/* Month View */}
                  {view === 'month' &&
                <div data-testid="month-view">
                      {/* Day headers */}
                      <div className="grid grid-cols-7 mb-2">
                        {DAYS.map((day) =>
                    <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                            {day}
                          </div>
                    )}
                      </div>
                      
                      {/* Calendar grid */}
                      <div className="grid grid-cols-7 border-t border-l">
                        {calendarDays.map(({ date, isCurrentMonth }, index) => {
                      const dayEvents = getEventsForDay(date);
                      const isToday = date.toDateString() === new Date().toDateString();

                      return (
                        <div
                          key={index}
                          className={`min-h-[100px] border-b border-r p-1 ${
                          isCurrentMonth ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'}`
                          }>
                          
                              <div className={`text-right text-sm mb-1 ${
                          isToday ?
                          'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center ml-auto' :
                          isCurrentMonth ?
                          'text-slate-900 dark:text-white' :
                          'text-slate-400'}`
                          }>
                                {date.getDate()}
                              </div>
                              
                              <div className="space-y-0.5">
                                {dayEvents.slice(0, 3).map(renderEventBadge)}
                                {dayEvents.length > 3 &&
                            <div className="text-xs text-slate-500 text-center">
                                    +{dayEvents.length - 3}{t("more", "more")}
                            </div>
                            }
                              </div>
                            </div>);

                    })}
                      </div>
                    </div>
                }

                  {/* Agenda View */}
                  {view === 'agenda' &&
                <div className="space-y-4" data-testid="agenda-view">
                      {events.length === 0 ?
                  <div className="text-center py-12 text-slate-500">
                          <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>{t("noEventsIn", "No events in this period")}</p>
                        </div> :

                  events.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)).map((event) => {
                    const category = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.other;
                    const Icon = category.icon;
                    const priority = PRIORITY_BADGES[event.priority] || PRIORITY_BADGES.normal;

                    return (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowEventDialog(true);
                        }}
                        className="flex items-start gap-4 p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        data-testid={`agenda-event-${event.id}`}>
                        
                              <div className={`w-10 h-10 rounded-lg ${category.bg} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-slate-900 dark:text-white truncate">
                                    {event.title}
                                  </h4>
                                  <Badge variant={priority.variant} className="text-xs">
                                    {priority.label}
                                  </Badge>
                                </div>
                                
                                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDate(event.start_time)} • {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                  </span>
                                  
                                  {event.location_text &&
                            <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {event.location_text}
                                    </span>
                            }
                                </div>
                                
                                {event.source_module &&
                          <Badge variant="outline" className="mt-2 text-xs">
                                    {event.source_module}
                                  </Badge>
                          }
                              </div>
                            </div>);

                  })
                  }
                    </div>
                }

                  {/* Week/Day views placeholder */}
                  {view === 'week' &&
                <div data-testid="week-view">
                      {/* Week view header with days */}
                      <div className="grid grid-cols-8 border-b">
                        <div className="p-2 text-xs font-medium text-slate-500"></div>
                        {(() => {
                      const weekStart = new Date(currentDate);
                      weekStart.setDate(currentDate.getDate() - currentDate.getDay());
                      return Array.from({ length: 7 }, (_, i) => {
                        const day = new Date(weekStart);
                        day.setDate(weekStart.getDate() + i);
                        const isToday = day.toDateString() === new Date().toDateString();
                        return (
                          <div key={i} className={`p-2 text-center border-l ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                                <div className="text-xs font-medium text-slate-500">{DAYS[i]}</div>
                                <div className={`text-lg font-semibold ${isToday ? 'text-blue-600' : 'text-slate-900 dark:text-white'}`}>
                                  {day.getDate()}
                                </div>
                              </div>);

                      });
                    })()}
                      </div>
                      
                      {/* Time slots */}
                      <div className="max-h-[500px] overflow-y-auto">
                        {Array.from({ length: 24 }, (_, hour) => {
                      const weekStart = new Date(currentDate);
                      weekStart.setDate(currentDate.getDate() - currentDate.getDay());

                      return (
                        <div key={hour} className="grid grid-cols-8 border-b min-h-[48px]">
                              <div className="p-1 text-xs text-slate-400 text-right pr-2 border-r">
                                {hour.toString().padStart(2, '0')}{t("00", ":00")}
                          </div>
                              {Array.from({ length: 7 }, (_, dayIndex) => {
                            const day = new Date(weekStart);
                            day.setDate(weekStart.getDate() + dayIndex);
                            day.setHours(hour, 0, 0, 0);

                            const hourEnd = new Date(day);
                            hourEnd.setHours(hour + 1, 0, 0, 0);

                            const hourEvents = events.filter((event) => {
                              const eventStart = new Date(event.start_time);
                              return eventStart >= day && eventStart < hourEnd;
                            });

                            const isToday = day.toDateString() === new Date().toDateString();

                            return (
                              <div
                                key={dayIndex}
                                className={`border-l p-0.5 ${isToday ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                                
                                    {hourEvents.map((event) => {
                                  const category = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.other;
                                  return (
                                    <div
                                      key={event.id}
                                      onClick={() => {
                                        setSelectedEvent(event);
                                        setShowEventDialog(true);
                                      }}
                                      className={`text-xs p-1 rounded cursor-pointer truncate ${category.bg} text-white hover:opacity-80`}
                                      title={event.title}>
                                      
                                          {event.title}
                                        </div>);

                                })}
                                  </div>);

                          })}
                            </div>);

                    })}
                      </div>
                    </div>
                }

                  {view === 'day' &&
                <div data-testid="day-view">
                      {/* Day header */}
                      <div className="text-center py-4 border-b">
                        <div className="text-lg font-semibold text-slate-900 dark:text-white">
                          {currentDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                        {currentDate.toDateString() === new Date().toDateString() &&
                    <Badge className="mt-1 bg-blue-600">{t("today", "Today")}</Badge>
                    }
                      </div>
                      
                      {/* Hourly slots */}
                      <div className="max-h-[500px] overflow-y-auto">
                        {Array.from({ length: 24 }, (_, hour) => {
                      const slotStart = new Date(currentDate);
                      slotStart.setHours(hour, 0, 0, 0);
                      const slotEnd = new Date(currentDate);
                      slotEnd.setHours(hour + 1, 0, 0, 0);

                      const slotEvents = events.filter((event) => {
                        const eventStart = new Date(event.start_time);
                        const eventDate = eventStart.toDateString();
                        const currentDateStr = currentDate.toDateString();
                        return eventDate === currentDateStr && eventStart.getHours() === hour;
                      });

                      const isCurrentHour = new Date().getHours() === hour &&
                      currentDate.toDateString() === new Date().toDateString();

                      return (
                        <div
                          key={hour}
                          className={`flex border-b min-h-[60px] ${isCurrentHour ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                          
                              <div className="w-20 p-2 text-sm text-slate-500 border-r flex-shrink-0">
                                {hour.toString().padStart(2, '0')}{t("00", ":00")}
                          </div>
                              <div className="flex-1 p-1 space-y-1">
                                {slotEvents.map((event) => {
                              const category = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.other;
                              const Icon = category.icon;
                              const priority = PRIORITY_BADGES[event.priority] || PRIORITY_BADGES.normal;

                              return (
                                <div
                                  key={event.id}
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setShowEventDialog(true);
                                  }}
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer ${category.bg} text-white hover:opacity-90`}>
                                  
                                      <Icon className="w-4 h-4 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{event.title}</div>
                                        <div className="text-xs opacity-80">
                                          {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                        </div>
                                      </div>
                                      {event.location_text &&
                                  <div className="text-xs opacity-80 flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {event.location_text}
                                        </div>
                                  }
                                    </div>);

                            })}
                              </div>
                            </div>);

                    })}
                      </div>
                    </div>
                }
                </>
              }
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Event Details Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          {selectedEvent &&
          <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {(() => {
                    const category = CATEGORY_COLORS[selectedEvent.category] || CATEGORY_COLORS.other;
                    const Icon = category.icon;
                    return (
                      <div className={`w-10 h-10 rounded-lg ${category.bg} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>);

                  })()}
                    <div>
                      <DialogTitle>{selectedEvent.title}</DialogTitle>
                      <p className="text-sm text-slate-500 capitalize">{selectedEvent.category}</p>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Time */}
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="font-medium">{formatDate(selectedEvent.start_time)}</p>
                    <p className="text-slate-500">
                      {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
                    </p>
                  </div>
                </div>
                
                {/* Location */}
                {selectedEvent.location_text &&
              <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{selectedEvent.location_text}</span>
                  </div>
              }
                
                {/* Description */}
                {selectedEvent.description &&
              <div className="text-sm">
                    <p className="font-medium mb-1">{t("description", "Description")}</p>
                    <p className="text-slate-600 dark:text-slate-400">{selectedEvent.description}</p>
                  </div>
              }
                
                {/* Source Module */}
                {selectedEvent.source_module &&
              <div className="flex items-center gap-2">
                    <Badge variant="outline">{selectedEvent.source_module}</Badge>
                    {selectedEvent.origin === 'auto_rule' &&
                <Badge variant="secondary">{t("autogenerated", "Auto-generated")}</Badge>
                }
                  </div>
              }
                
                {/* Priority */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t("priority", "Priority:")}</span>
                  <Badge variant={PRIORITY_BADGES[selectedEvent.priority]?.variant || 'outline'}>
                    {PRIORITY_BADGES[selectedEvent.priority]?.label || selectedEvent.priority}
                  </Badge>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                variant="destructive"
                onClick={() => handleDeleteEvent(selectedEvent.id)}
                data-testid="delete-event-btn">{t("delete", "Delete")}


              </Button>
                <Button variant="outline" onClick={() => setShowEventDialog(false)}>{t("close", "Close")}

              </Button>
              </DialogFooter>
            </>
          }
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createNewEvent", "Create New Event")}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("calendar", "Calendar *")}</Label>
              <Select
                value={newEvent.calendar_id}
                onValueChange={(val) => setNewEvent((prev) => ({ ...prev, calendar_id: val }))}>
                
                <SelectTrigger className="mt-1" data-testid="new-event-calendar">
                  <SelectValue placeholder="Select calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) =>
                  <SelectItem key={cal.id} value={cal.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cal.color }} />
                        {cal.name}
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t("title", "Title *")}</Label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Event title"
                className="mt-1"
                data-testid="new-event-title" />
              
            </div>
            
            <div>
              <Label>{t("description", "Description")}</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Event description"
                className="mt-1"
                rows={3}
                data-testid="new-event-description" />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("startTime", "Start Time *")}</Label>
                <Input
                  type="datetime-local"
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent((prev) => ({ ...prev, start_time: e.target.value }))}
                  className="mt-1"
                  data-testid="new-event-start" />
                
              </div>
              <div>
                <Label>{t("endTime", "End Time *")}</Label>
                <Input
                  type="datetime-local"
                  value={newEvent.end_time}
                  onChange={(e) => setNewEvent((prev) => ({ ...prev, end_time: e.target.value }))}
                  className="mt-1"
                  data-testid="new-event-end" />
                
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("category", "Category")}</Label>
                <Select
                  value={newEvent.category}
                  onValueChange={(val) => setNewEvent((prev) => ({ ...prev, category: val }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-event-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receiving">{t("receiving", "Receiving")}</SelectItem>
                    <SelectItem value="shipping">{t("shipping", "Shipping")}</SelectItem>
                    <SelectItem value="finance">{t("finance", "Finance")}</SelectItem>
                    <SelectItem value="compliance">{t("compliance", "Compliance")}</SelectItem>
                    <SelectItem value="delivery">{t("delivery", "Delivery")}</SelectItem>
                    <SelectItem value="meeting">{t("meeting", "Meeting")}</SelectItem>
                    <SelectItem value="reminder">{t("reminder", "Reminder")}</SelectItem>
                    <SelectItem value="other">{t("other", "Other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("priority", "Priority")}</Label>
                <Select
                  value={newEvent.priority}
                  onValueChange={(val) => setNewEvent((prev) => ({ ...prev, priority: val }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-event-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low", "Low")}</SelectItem>
                    <SelectItem value="normal">{t("normal", "Normal")}</SelectItem>
                    <SelectItem value="high">{t("high", "High")}</SelectItem>
                    <SelectItem value="critical">{t("critical", "Critical")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{t("location", "Location")}</Label>
              <Input
                value={newEvent.location_text}
                onChange={(e) => setNewEvent((prev) => ({ ...prev, location_text: e.target.value }))}
                placeholder="Event location"
                className="mt-1"
                data-testid="new-event-location" />
              
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newEvent.all_day}
                onCheckedChange={(checked) => setNewEvent((prev) => ({ ...prev, all_day: checked }))}
                id="all-day" />
              
              <Label htmlFor="all-day" className="font-normal cursor-pointer">{t("alldayEvent", "All-day event")}</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleCreateEvent}
              disabled={!newEvent.calendar_id || !newEvent.title || !newEvent.start_time || !newEvent.end_time}
              data-testid="create-event-submit">{t("createEvent", "Create Event")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}